import * as THREE from 'three';
import type { GameContext } from '../core/context';
import { clamp, damp } from '../core/math';
import type { System } from '../core/system';
import { carTraits, glyphCodes, stripeFor, withLook, CAR_LOOK_KEY, CAR_LOOK_VERSION_KEY, type CarLook } from './carTraits';
import {
  carTime,
  carWet,
  createCarMaterial,
  createGlassMaterial,
  createLightMaterial,
  makeCarUniforms,
  makeGlassUniforms,
  makeLightUniforms,
  releaseCarMaterial,
} from './carShader';
import { warmAlongside } from '../render/shaderWarmup';
import { lampLevels, looseParts, makeLampMemory, paneState, type LooseParts } from './lamps';
import { POOL_GEOMETRY, POOL_MATERIALS, WHEEL_STYLE_IDS, getVehicleParts, type WheelPlacement, type WheelStyle } from './meshes';
import { MESHY_CAR_MODELS, MESHY_SECONDARY, meshyCarParts, meshyCars, requestMeshyCars, type MeshyCarParts } from './meshyCar';
import type { Vehicle } from './types';
import type { VehicleService } from './vehicleSystem';
import { wheelStance } from '../../shared/garage';
import { allocShadow, attachShadowBatches, detachShadowBatches, freeShadow, setShadowMatrix, shadowBatch, syncShadowBatches, type ShadowSlot } from './shadowBatch';
import { allocWheel, attachWheelBatches, detachWheelBatches, flushWheelBatches, freeWheel, setWheelBurnt, setWheelDetail, setWheelMatrix, setWheelRim, type WheelSlot } from './wheelBatch';

/**
 * Vehicle visuals: one merged body mesh + one glass mesh per car (meshes.ts), wheels instanced
 * across all cars (wheelBatch.ts), and an additive light mesh for headlight pools/beams and brake
 * glow. Per frame the rig interpolates the pose, steers and spins the wheels, rolls/pitches the
 * body from telemetry, and pushes lamp levels, the driver, loose parts, glass damage and burn into
 * the car's shader uniforms; dents still crumple a per-car copy of the body geometry.
 *
 * World-artist may override the whole look with a `vehicleMeshFactory` service.
 */

let detailedWarmed = false;

/**
 * Hand the detailed body's textured program (createCarMaterial with maps: one program for every detailed car) to
 * the boot pre-warm, from the first car view: the models land a few seconds into play on a slow line, and that
 * program compiled on the frame the first one appeared (render/shaderWarmup.ts warmAlongside). Placeholder maps:
 * the program does not depend on them.
 */
function warmDetailedProgram(geometry: THREE.BufferGeometry, u: ReturnType<typeof makeCarUniforms>): void {
  if (detailedWarmed) return;
  detailedWarmed = true;
  const texel = (): THREE.DataTexture => {
    const t = new THREE.DataTexture(new Uint8Array([128, 128, 128, 255]), 1, 1);
    t.needsUpdate = true;
    return t;
  };
  warmAlongside(new THREE.Mesh(geometry, createCarMaterial(u, { color: texel(), mask: texel() })));
}

export interface VehicleRig {
  root: THREE.Object3D;
  /** Child that receives body roll / pitch. */
  body: THREE.Object3D;
  /** Steerable front wheel pivots (empty groups; the wheels themselves are instanced). */
  frontWheels: THREE.Object3D[];
  /** Called every frame after the transform update. */
  sync(v: Vehicle, dt: number, time: number): void;
  dispose(): void;
}

export type VehicleMeshFactory = (v: Vehicle) => VehicleRig;

declare module '../core/hooks' {
  interface BhHooks {
    /**
     * Detailed (Meshy) car bodies (meshyCar.ts): `on` false draws every car procedurally, for before/after
     * measurements (not a setting). Returns how many cars draw a detailed body now.
     */
    detailedCars(on?: boolean): number;
  }
}

/** Debug switch behind the `detailedCars` hook (measurements only). */
export const meshyCarsEnabled = { on: true, version: 0 };
let detailedNow = 0;

declare module '../core/services' {
  interface ServiceMap {
    vehicleMeshFactory: VehicleMeshFactory;
  }
}

/** Camera position for the distance LOD (the render system copies the camera here each frame). */
export const lodCamera = new THREE.Vector3(0, 1e6, 0);
/**
 * True while a render system draws the scene: it then packs the wheel batches itself, right before it
 * draws, culled to that frame's view (`flushWheelBatches(frustum)`). Headless, the views pack them.
 */
export const lodView = { on: false };

/**
 * One car shown on its own in another scene (Pike's studio, src/garage/studio.ts): while `id` and `scene` are set
 * that car's view lives in `scene`, the wheel batches go with it and every other car's wheels are hidden (the
 * batches are shared). Whoever sets it clears it; everything goes back to the city scene on the next frame.
 */
export const vehicleSolo: { id: number | null; scene: THREE.Object3D | null } = { id: null, scene: null };
/** Beyond this (per detail tier) a car draws the far impostor only (no glass, wheels or light). */
const FAR_DIST2 = [80 * 80, 130 * 130, 180 * 180];
/** HIGH only: beyond this, cars drop to the mid body (no interior, driver or small trim). */
const MID_DIST2 = 60 * 60;

/** Scene-wide lighting facts the render layer feeds in (mood darkness, rain, quality). */
export const vehicleAmbient = {
  /** 0 day .. 1 night: how much headlights and pools show. */
  night: 1,
  /**
   * How much the headlamp pools show on the road (0..1, on top of `night`): full in the dark, about half
   * at a drizzly dusk, where the sky still lights the road and a full pool read as a grey fog patch ahead
   * of every car on a phone (docs/ART-DIRECTION.md). The lamps themselves keep following `night`.
   */
  pools: 1,
  /** 0 dry .. 1 heavy rain: beams scatter more in wet air, paint gets a water film. */
  wet: 0.3,
  /** Volumetric beams (HIGH quality only). */
  beams: true,
  /** Body detail tier: 2 = HIGH (full sculpt near the camera), 1 = MEDIUM, 0 = LOW (mid body, shorter far distance). */
  tier: 2 as 0 | 1 | 2,
};

/** Push vertices near each dent inward (toward the centre line) and down. */
export function crumpleInto(target: THREE.BufferAttribute, original: Float32Array, dents: readonly { x: number; z: number; depth: number }[], halfW: number, halfL: number): void {
  const arr = target.array as Float32Array;
  arr.set(original);
  for (const d of dents) {
    const radius = 0.7 + d.depth * 2.2;
    const r2 = radius * radius;
    const nx = Math.abs(d.x) / halfW;
    const nz = Math.abs(d.z) / halfL;
    let ix = 0;
    let iz = 0;
    if (nz >= nx) iz = -Math.sign(d.z);
    else ix = -Math.sign(d.x);
    for (let i = 0; i < arr.length; i += 3) {
      const dx = arr[i]! - d.x;
      const dz = arr[i + 2]! - d.z;
      const q = dx * dx + dz * dz;
      if (q > r2) continue;
      const f = 1 - Math.sqrt(q) / radius;
      const k = f * f * d.depth;
      arr[i] = arr[i]! + ix * k;
      arr[i + 2] = arr[i + 2]! + iz * k;
      arr[i + 1] = Math.max(0.05, arr[i + 1]! - k * 0.35 * (arr[i + 1]! > 0.6 ? 1 : 0.3));
    }
  }
  target.needsUpdate = true;
}

/** A dent copy of the shared body: own positions/normals, colour/surf/uv attributes shared. */
function cloneForDents(base: THREE.BufferGeometry): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', (base.getAttribute('position') as THREE.BufferAttribute).clone());
  g.setAttribute('normal', (base.getAttribute('normal') as THREE.BufferAttribute).clone());
  for (const name of ['uv', 'color', 'surf']) g.setAttribute(name, base.getAttribute(name));
  // Detailed (Meshy) bodies are indexed: share the index, and the dents get smooth normals.
  if (base.index) g.setIndex(base.index);
  g.boundingSphere = base.boundingSphere?.clone() ?? null;
  if (!g.boundingSphere) g.computeBoundingSphere();
  return g;
}

function disposeDentCopy(g: THREE.BufferGeometry): void {
  // Shared attributes belong to the cached body; drop them first so their GPU buffers survive.
  for (const name of ['uv', 'color', 'surf']) g.deleteAttribute(name);
  g.setIndex(null);
  g.dispose();
}

const _m = new THREE.Matrix4();
const _t = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _s = new THREE.Vector3();
const _p = new THREE.Vector3();
const _w = new THREE.Vector3();

/**
 * The rig's matrices are kept by hand (matrixAutoUpdate off on every object in it): three would otherwise
 * recompose ~10 matrices per car every frame, ~620 on a busy street, though most cars are parked and most
 * parts never move inside the car. `keepMatrix` recomposes an object's matrix only when its position,
 * rotation or scale changed since the pose remembered in `last` (10 numbers, NaN at first).
 */
function keepMatrix(o: THREE.Object3D, last: Float64Array): void {
  const p = o.position;
  const q = o.quaternion;
  const sc = o.scale;
  if (last[0] === p.x && last[1] === p.y && last[2] === p.z && last[3] === q.x && last[4] === q.y && last[5] === q.z && last[6] === q.w && last[7] === sc.x && last[8] === sc.y && last[9] === sc.z) return;
  last[0] = p.x;
  last[1] = p.y;
  last[2] = p.z;
  last[3] = q.x;
  last[4] = q.y;
  last[5] = q.z;
  last[6] = q.w;
  last[7] = sc.x;
  last[8] = sc.y;
  last[9] = sc.z;
  o.updateMatrix();
}

/** A part that never moves inside the car: its matrix is composed once (see keepMatrix). */
function fixMatrix(o: THREE.Object3D): void {
  o.matrixAutoUpdate = false;
  o.updateMatrix();
}

function hingeMatrix(out: THREE.Matrix4, axis: 'x' | 'z', angle: number, px: number, py: number, pz: number): THREE.Matrix4 {
  if (angle === 0) return out.identity();
  out.makeTranslation(px, py, pz);
  _t.copy(axis === 'x' ? _m.makeRotationX(angle) : _m.makeRotationZ(angle));
  out.multiply(_t);
  out.multiply(_m.makeTranslation(-px, -py, -pz));
  return out;
}

/** Half the tyre's width in the unit wheel geometry (meshes.ts wheelGeometry: the tread spans x ±0.5). */
const TYRE_HALF = 0.5;

/**
 * One wheel's instance matrix in the car's own frame. Ry(steer) · Rz(camber) · Rx(spin): the hub faces out on
 * both sides, so +camber leans each top inward. A leaning tyre stands on its lower edge, not its centre line:
 * the hub is lifted by that much so the tyre still touches the road (no sinking at any camber).
 */
export function wheelLocalMatrix(
  w: WheelPlacement,
  stance: { camber: number; offset: number },
  spin: number,
  steer: number,
  sink: number,
  shrink: number,
  out: THREE.Matrix4,
): THREE.Matrix4 {
  const r = w.radius * shrink;
  const lift = r * Math.cos(stance.camber) + w.width * TYRE_HALF * Math.sin(Math.abs(stance.camber)) - r;
  _e.set(w.side > 0 ? spin : -spin, steer + (w.side > 0 ? 0 : Math.PI), stance.camber, 'YZX');
  _q.setFromEuler(_e);
  _p.set(w.pos.x + Math.sign(w.pos.x) * stance.offset, w.pos.y - sink + lift, w.pos.z);
  _s.set(w.width, r, r);
  return out.compose(_p, _q, _s);
}

const WHEEL_STYLES = new Set<string>(WHEEL_STYLE_IDS);

/** Ride height offsets (m) for `CarLook.ride` -1 / +1: the body sits lower or higher over its wheels. */
export const RIDE_DROP = -0.07;
const RIDE_LIFT = 0.08;

/**
 * Underglow footprint: full strength out to the car's sides (where the tubes would be), then a soft fall-off
 * onto the road. Generated once, no DOM needed.
 */
let neonTex: THREE.DataTexture | null = null;
function neonTexture(): THREE.DataTexture {
  if (neonTex) return neonTex;
  const n = 64;
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      // Rounded-rectangle distance in uv space (0 inside the car's footprint, 1 at the edge of the quad).
      const dx = Math.max(0, Math.abs((x + 0.5) / n - 0.5) * 2 - 0.55) / 0.45;
      const dy = Math.max(0, Math.abs((y + 0.5) / n - 0.5) * 2 - 0.62) / 0.38;
      const d = Math.min(1, Math.hypot(dx, dy));
      const a = Math.pow(1 - d, 1.8);
      const i = (y * n + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 255;
      data[i + 3] = Math.round(a * 255);
    }
  }
  neonTex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  neonTex.needsUpdate = true;
  return neonTex;
}

/** Underglow materials, one per colour (shared by every car wearing it): additive, faded by the hour. */
const neonMats = new Map<number, THREE.MeshBasicMaterial>();
function neonMaterial(hex: number): THREE.MeshBasicMaterial {
  let m = neonMats.get(hex);
  if (!m) {
    m = new THREE.MeshBasicMaterial({ map: neonTexture(), color: hex, transparent: true, opacity: 0.6, blending: THREE.AdditiveBlending, depthWrite: false });
    neonMats.set(hex, m);
  }
  return m;
}
let neonNight = -1;
/** Once per frame: neon reads faint by day and bright after dark. */
function syncNeon(night: number): void {
  if (Math.abs(night - neonNight) < 0.01) return;
  neonNight = night;
  for (const m of neonMats.values()) m.opacity = 0.4 + 0.6 * night;
}

export const defaultVehicleMeshFactory: VehicleMeshFactory = (v) => {
  const spec = v.spec;
  const parts = getVehicleParts(spec);
  // A workshop look (Halloran's, src/activities) overrides the seeded traits and the wheel style.
  const look = v.data[CAR_LOOK_KEY] as CarLook | undefined;
  const traits = withLook(carTraits(v.id, spec), spec, look);
  const wheelStyle: WheelStyle = look?.wheels && WHEEL_STYLES.has(look.wheels) ? (look.wheels as WheelStyle) : parts.wheelStyle;
  const root = new THREE.Group();
  root.name = `vehicle-${v.id}`;
  const body = new THREE.Group();
  root.add(body);
  // The car's pose (whoever sets it: vehicleView, the profile yard) and the body's roll, pitch and ride height are
  // recomposed in sync() when they changed (keepMatrix).
  root.matrixAutoUpdate = false;
  body.matrixAutoUpdate = false;
  const rootPose = new Float64Array(10).fill(NaN);
  const bodyPose = new Float64Array(10).fill(NaN);

  // --- per-car shader state
  const u = makeCarUniforms();
  u.uPaint.value.setHex(v.paint);
  const secondary = spec.body === 'lorry' ? traits.bed : traits.stripes !== null ? stripeFor(traits.stripes, v.paint) : traits.band;
  if (secondary !== null) u.uPaint2.value.setHex(secondary);
  const roofMode = traits.vinyl !== null ? 2 : traits.roof !== null ? 1 : 0;
  if (roofMode) u.uRoofCol.value.setHex((traits.vinyl ?? traits.roof)!);
  u.uShow.value.set(0, traits.rack ? 1 : 0, roofMode, secondary !== null ? 1 : 0);
  u.uTrimCol.value.setHex(traits.trim);
  u.uCoat.value.setHex(traits.driverCoat);
  u.uSkin.value.setHex(traits.driverSkin);
  u.uWear.value.set(traits.grime, traits.age, (v.id % 97) * 0.37, parts.clearance);
  u.uArch.value.set(...parts.arch);
  const metal = traits.metallic && !look?.matt;
  u.uFinish.value.set(metal ? 1 : 0, metal ? 0.6 + 0.4 * (1 - traits.age) : 0, look?.matt ? 1 : 0, 0);
  glyphCodes(traits.plate, u.uPlate.value as unknown as number[]);
  glyphCodes(v.isPolice ? 'POLICE' : 'TAXI', u.uSign.value as unknown as number[]);
  if (parts.beacon) u.uBeacon.value.copy(parts.beacon);
  const material = createCarMaterial(u);
  /** The textured programs for a detailed body, one per LOD atlas (made when one first shows on this car). */
  let matHigh: THREE.MeshPhysicalMaterial | null = null;
  let matMid: THREE.MeshPhysicalMaterial | null = null;
  const gu = makeGlassUniforms();
  const glassMat = createGlassMaterial(gu);
  const lu = makeLightUniforms();
  const lightMat = createLightMaterial(lu);

  // Detailed (Meshy) body for this body style (meshyCar.ts): MEDIUM and HIGH, once loaded; LOW stays procedural.
  const hasMeshy = MESHY_CAR_MODELS[spec.body] !== undefined;
  if (hasMeshy) warmDetailedProgram(parts.mid, u);
  let meshy: MeshyCarParts | null = null;
  /** HIGH draws the detailed body once its own atlas is in (until then, and on MEDIUM, the mid body). */
  let meshyHigh = false;
  let seenMeshy = -1;
  let wasSolo = false;
  const secondaryShown = secondary !== null;
  const paint2 = u.uPaint2.value.getHex();
  function pickMeshy(): void {
    seenMeshy = meshyCars.version + meshyCarsEnabled.version;
    if (hasMeshy && vehicleAmbient.tier > 0) requestMeshyCars(vehicleAmbient.tier === 2 || vehicleSolo.id === v.id);
    meshy = hasMeshy && vehicleAmbient.tier > 0 && meshyCarsEnabled.on ? meshyCarParts(spec) : null;
    // Geometry without its textures cannot be painted.
    if (meshy && !meshy.maps.mid) meshy = null;
    meshyHigh = meshy !== null && meshy.maps.high !== null;
    if (meshy) matMid ??= createCarMaterial(u, meshy.maps.mid);
    if (meshyHigh) matHigh ??= createCarMaterial(u, meshy!.maps.high);
    u.uMeshy.value.set(99, 0, 0, 0);
    // The patrol car's blue band is painted by the shader on a detailed body (slot 2 = uPaint2).
    u.uShow.value.w = secondaryShown ? 1 : 0;
    u.uPaint2.value.setHex(paint2);
    if (meshy) {
      u.uMeshy.value.copy(meshy.meshy);
      if (meshy.meshy.w === MESHY_SECONDARY.PATROL) {
        u.uPaint2.value.setHex(0x1d3f8f);
        u.uShow.value.w = 1;
      }
    }
  }

  /** Full detail: HIGH, or a detailed body on show on its own in Pike's studio (close up, any tier but LOW). */
  const detail = (): boolean => vehicleAmbient.tier === 2 || (meshy !== null && vehicleSolo.id === v.id);
  /** Near body for the current detail tier (HIGH: full sculpt; MEDIUM/LOW: the mid body). */
  const nearBase = (): THREE.BufferGeometry =>
    detail() ? (meshy ? (meshyHigh ? meshy.body : meshy.mid) : parts.body) : (meshy?.mid ?? parts.mid);
  const glassFor = (t: number): THREE.BufferGeometry =>
    t === 2 || detail() ? (meshy ? (meshyHigh ? meshy.glass : meshy.glassMid) : parts.glass) : (meshy?.glassMid ?? parts.glassMid);
  /** The program that goes with a body geometry (each detailed LOD has its own atlas). */
  const materialFor = (g: THREE.BufferGeometry): THREE.Material => {
    if (!meshy) return material;
    const base = g === paintGeo && ownsPaintGeo ? dentBase : g;
    return base === meshy.body ? matHigh! : base === meshy.mid ? matMid! : material;
  };
  let paintGeo = parts.body;
  let ownsPaintGeo = false;
  let dentBase: THREE.BufferGeometry | null = null;
  let originalPaint: Float32Array | null = null;
  let tier = vehicleAmbient.tier;
  const bodyMesh: THREE.Mesh = new THREE.Mesh(paintGeo, material);
  pickMeshy();
  paintGeo = nearBase();
  bodyMesh.geometry = paintGeo;
  bodyMesh.material = materialFor(paintGeo);
  bodyMesh.name = 'body';
  // Only the body casts a shadow (glass and wheels sit inside it): one shadow-pass draw per car, or (shadowBatch.ts)
  // one instance in the car-shadow batch for its mid body, so a whole street of shadows is a handful of draws.
  const batchedShadow = shadowBatch.on;
  let shadow: ShadowSlot | null = null;
  bodyMesh.castShadow = !batchedShadow;
  bodyMesh.receiveShadow = true;
  body.add(bodyMesh);
  fixMatrix(bodyMesh);
  const glassMesh = new THREE.Mesh(glassFor(tier), glassMat);
  glassMesh.name = 'glass';
  glassMesh.renderOrder = 1;
  body.add(glassMesh);
  fixMatrix(glassMesh);
  const lightMesh = new THREE.Mesh(parts.lights, lightMat);
  lightMesh.name = 'lights';
  lightMesh.renderOrder = 3;
  lightMesh.visible = false;
  root.add(lightMesh);
  fixMatrix(lightMesh);

  let sirenPool: THREE.Mesh | null = null;
  if (parts.beacon) {
    sirenPool = new THREE.Mesh(POOL_GEOMETRY, POOL_MATERIALS.sirenBlue);
    sirenPool.scale.set(8, 1, 8);
    sirenPool.position.y = 0.05;
    sirenPool.renderOrder = 3;
    sirenPool.visible = false;
    root.add(sirenPool);
    fixMatrix(sirenPool); // recomposed in sync() while it turns
  }
  const firePool = new THREE.Mesh(POOL_GEOMETRY, POOL_MATERIALS.fire);
  firePool.scale.set(9, 1, 9);
  firePool.position.y = 0.05;
  firePool.visible = false;
  firePool.renderOrder = 3;
  root.add(firePool);
  fixMatrix(firePool); // recomposed in sync() while it flickers

  // Pike's spray bay: an underglow (one small additive pool under the car; no real light) and the ride height.
  let neon: THREE.Mesh | null = null;
  if (look?.neon !== undefined) {
    neon = new THREE.Mesh(POOL_GEOMETRY, neonMaterial(look.neon));
    neon.name = 'neon';
    neon.scale.set(spec.width + 2.2, 1, spec.length + 2.2);
    // Just above pavement height (0.12): visible in a paved yard as well as on the road, always under the sills.
    neon.position.y = 0.15;
    neon.renderOrder = 3;
    root.add(neon);
    fixMatrix(neon);
  }
  const ride = look?.ride === -1 ? RIDE_DROP : look?.ride === 1 ? RIDE_LIFT : 0;
  // Camber and track (shared/garage.ts wheelStance: combinations that would rub are tucked out of the way).
  const stance = wheelStance({ ride: look?.ride ?? 0, camber: look?.camber ?? 0, track: look?.track ?? 0 }, parts.wheelRadius);

  // --- instanced wheels
  const slots: (WheelSlot | null)[] = parts.wheels.map(() => allocWheel(wheelStyle));
  if (look?.rim !== undefined) for (const s of slots) if (s) setWheelRim(s, look.rim);
  const frontWheels: THREE.Object3D[] = parts.wheels
    .filter((w) => w.steer)
    .map((w) => {
      const pivot = new THREE.Group();
      pivot.position.copy(w.pos);
      root.add(pivot);
      fixMatrix(pivot); // recomposed in sync() when the steering moves
      return pivot;
    });
  let steerShown = 0;
  let spin = 0;
  let wheelsBurnt = false;

  let roll = 0;
  let pitch = 0;
  let seenDamage = -1;
  let wrecked = false;
  let far = false;
  let burn = 0;
  const mem = makeLampMemory();
  const loose: LooseParts = { bonnet: 0, boot: 0, bumperF: 0, bumperR: 0 };
  const panes = [0, 0, 0, 0];
  const env = { night: 1, time: 0, dt: 0 };
  let paint = v.paint;

  function applyDamageShape(veh: Vehicle): void {
    const base = nearBase();
    if (veh.dents.length > 0) {
      if (ownsPaintGeo && dentBase !== base) {
        disposeDentCopy(paintGeo);
        ownsPaintGeo = false;
      }
      if (!ownsPaintGeo) {
        paintGeo = cloneForDents(base);
        originalPaint = (base.getAttribute('position').array as Float32Array).slice();
        ownsPaintGeo = true;
        dentBase = base;
      }
      crumpleInto(paintGeo.getAttribute('position') as THREE.BufferAttribute, originalPaint!, veh.dents, spec.width / 2, spec.length / 2);
      paintGeo.computeVertexNormals();
    } else {
      if (ownsPaintGeo) disposeDentCopy(paintGeo);
      paintGeo = base;
      ownsPaintGeo = false;
      dentBase = null;
      originalPaint = null;
    }
    looseParts(veh, traits.hangSide, loose);
    const P = u.uPart.value;
    const bonnetHinge = meshy?.bonnetHinge ?? parts.bonnetHinge;
    const bootHinge = meshy?.bootHinge ?? parts.bootHinge;
    if (bonnetHinge) hingeMatrix(P[0]!, 'x', -loose.bonnet, 0, bonnetHinge[1], bonnetHinge[0]);
    if (bootHinge) hingeMatrix(P[3]!, 'x', loose.boot, 0, bootHinge[1], bootHinge[0]);
    const [fz, fy, fw] = meshy?.bumperF ?? parts.bumperF;
    const [rz, ry, rw] = meshy?.bumperR ?? parts.bumperR;
    hingeMatrix(P[1]!, 'z', -loose.bumperF, -Math.sign(loose.bumperF) * fw, fy, fz);
    hingeMatrix(P[2]!, 'z', -loose.bumperR, -Math.sign(loose.bumperR) * rw, ry, rz);
    paneState(veh, panes);
    gu.uPane.value.set(panes[0]!, panes[1]!, panes[2]!, panes[3]!);
  }

  /** Last wheel pose written, so parked cars don't rewrite their instance matrices every frame. */
  const lastWheel = { x: NaN, z: NaN, a: NaN, spin: NaN, steer: NaN, wreck: false, shown: false };
  function syncWheels(veh: Vehicle, dt: number, show: boolean): void {
    if (!show) {
      if (lastWheel.shown) for (const s of slots) if (s) setWheelMatrix(s, null);
      lastWheel.shown = false;
      return;
    }
    const tel = veh.telemetry;
    const R = parts.wheelRadius;
    spin = (spin + (tel.forwardSpeed / R) * dt) % (Math.PI * 2);
    const L = lastWheel;
    if (L.shown && L.x === root.position.x && L.z === root.position.z && L.a === root.rotation.y && L.spin === spin && L.steer === tel.wheelAngle && L.wreck === wrecked) return;
    L.x = root.position.x;
    L.z = root.position.z;
    L.a = root.rotation.y;
    L.spin = spin;
    L.steer = tel.wheelAngle;
    L.wreck = wrecked;
    L.shown = true;
    const sink = wrecked ? 0.07 : 0;
    const shrink = wrecked ? 0.88 : 1;
    if (wrecked !== wheelsBurnt) {
      wheelsBurnt = wrecked;
      for (const s of slots) if (s) setWheelBurnt(s, wrecked);
    }
    for (let i = 0; i < parts.wheels.length; i++) {
      const s = slots[i];
      if (!s) continue;
      const w = parts.wheels[i]!;
      wheelLocalMatrix(w, stance, spin, w.steer ? -tel.wheelAngle : 0, sink, shrink, _m);
      _m.premultiply(root.matrix);
      setWheelMatrix(s, _m);
    }
  }

  /** Last body pose written to the shadow batch (parked cars don't rewrite it every frame). */
  const lastShadow = { x: NaN, z: NaN, a: NaN, pitch: NaN, roll: NaN, y: NaN };
  function syncShadow(solo: boolean): void {
    // On show in the studio the body casts its own shadow there (the batch lives in the city scene).
    bodyMesh.castShadow = solo;
    const near = meshy?.mid ?? parts.mid;
    if (shadow && shadow.near !== near) {
      freeShadow(shadow);
      shadow = null;
    }
    if (solo) {
      if (shadow) setShadowMatrix(shadow, null);
      lastShadow.x = NaN;
      return;
    }
    shadow ??= allocShadow(near, parts.far);
    const L = lastShadow;
    if (L.x === root.position.x && L.z === root.position.z && L.a === root.rotation.y && L.pitch === pitch && L.roll === roll && L.y === body.position.y && shadow.shown) return;
    L.x = root.position.x;
    L.z = root.position.z;
    L.a = root.rotation.y;
    L.pitch = pitch;
    L.roll = roll;
    L.y = body.position.y;
    setShadowMatrix(shadow, _m.multiplyMatrices(root.matrix, body.matrix));
  }

  return {
    root,
    body,
    frontWheels,
    sync(veh, dt, time) {
      const tel = veh.telemetry;
      // Weight transfer: roll away from the turn, squat/dive with acceleration.
      const k = damp(7, dt);
      const soft = spec.body === 'van' || spec.body === 'lorry' ? 1.5 : spec.body === 'coupe' ? 0.6 : 1;
      roll += (clamp(-tel.latAccel * 0.0065 * soft, -0.1, 0.1) - roll) * k;
      pitch += (clamp(-tel.longAccel * 0.006 * soft, -0.07, 0.07) - pitch) * k;
      body.rotation.set(pitch, 0, roll);
      if (-tel.wheelAngle !== steerShown) {
        steerShown = -tel.wheelAngle;
        for (const w of frontWheels) {
          w.rotation.y = steerShown;
          w.updateMatrix();
        }
      }

      const isWreck = veh.condition === 'wrecked';
      // A tier change, or a detailed body that just finished loading, swaps the body, glass and program.
      const solo = hasMeshy && vehicleSolo.id === veh.id;
      const tierChanged = vehicleAmbient.tier !== tier || (hasMeshy && (meshyCars.version + meshyCarsEnabled.version !== seenMeshy || solo !== wasSolo));
      wasSolo = solo;
      if (tierChanged) {
        tier = vehicleAmbient.tier;
        pickMeshy();
        glassMesh.geometry = glassFor(tier);
      }
      if (veh.damageVersion !== seenDamage || isWreck !== wrecked || tierChanged) {
        seenDamage = veh.damageVersion;
        wrecked = isWreck;
        applyDamageShape(veh);
        body.position.y = (isWreck ? -0.12 : 0) + ride;
      }
      keepMatrix(root, rootPose);
      keepMatrix(body, bodyPose);
      if (veh.paint !== paint) {
        paint = veh.paint;
        u.uPaint.value.setHex(paint);
        if (traits.stripes !== null) u.uPaint2.value.setHex(stripeFor(traits.stripes, paint));
      }
      // Fire chars the paint progressively; a wreck is fully burnt out.
      const burnTarget = isWreck ? 1 : veh.condition === 'burning' ? 0.75 : 0;
      burn = burnTarget < burn ? burnTarget : Math.min(burnTarget, burn + dt * 0.12);
      u.uBurn.value = burn;
      gu.uBurn.value = burn;

      env.night = vehicleAmbient.night;
      env.time = time;
      env.dt = dt;
      lampLevels(veh, env, mem, u.uLamp.value);
      u.uShow.value.x = veh.occupant !== null && !veh.destroyed ? 1 : 0;

      // Distance LOD: far cars draw only the impostor body; on HIGH the mid body takes over at 60 m.
      // root.matrix is current (keepMatrix above); getWorldPosition would recompose the scene's matrix per car.
      _w.setFromMatrixPosition(root.matrix);
      if (root.parent) _w.applyMatrix4(root.parent.matrixWorld);
      const d2 = _w.distanceToSquared(lodCamera);
      const nowFar = d2 > FAR_DIST2[tier]!;
      if (nowFar !== far) {
        far = nowFar;
        glassMesh.visible = !far;
      }
      const wantGeo = far ? parts.far : tier === 2 && d2 > MID_DIST2 && !ownsPaintGeo ? (meshy?.mid ?? parts.mid) : paintGeo;
      if (meshy && !far) detailedNow++;
      if (bodyMesh.geometry !== wantGeo) {
        bodyMesh.geometry = wantGeo;
        bodyMesh.material = materialFor(wantGeo);
      }
      // Wheels: never on the impostor (the batches draw only shown slots, and of those only the ones in view).
      syncWheels(veh, dt, !far && (vehicleSolo.id === null || vehicleSolo.id === veh.id));
      if (batchedShadow) syncShadow(vehicleSolo.id === veh.id);

      // Light on the road: head pools/beams, brake glow, reversing light.
      const lamps = u.uLamp.value;
      const night = vehicleAmbient.night;
      // Kept soft: a warm wash on the road ahead, not a white blob (eased in, so dusk only hints at it).
      const head = lamps[1]! > 0 ? 0.8 * night * night * (3 - 2 * night) * vehicleAmbient.pools : 0;
      lu.uLevel.value.set(
        head,
        vehicleAmbient.beams ? head * (0.25 + 0.6 * vehicleAmbient.wet) : 0,
        lamps[2]! > 1.5 ? 0.22 + 0.4 * night : lamps[2]! > 0.2 ? 0.25 * night : 0,
        lamps[7]! > 0 ? 0.25 + 0.5 * night : 0,
      );
      const lv = lu.uLevel.value;
      // On show in the studio (vehicleSolo) the road pools and beams are left off: there is no road to light.
      lightMesh.visible = !far && vehicleSolo.id !== veh.id && lv.x + lv.y + lv.z + lv.w > 0.02;
      if (neon) neon.visible = !far && !veh.destroyed;

      if (sirenPool) {
        const on = veh.siren && !veh.destroyed;
        sirenPool.visible = on;
        if (on) {
          // Follows the rotating lamp's lobe round the car.
          const a = time * 9;
          sirenPool.position.x = Math.sin(a) * 2.4;
          sirenPool.position.z = Math.cos(a) * 2.4;
          sirenPool.updateMatrix();
        }
      }
      const burning = veh.condition === 'burning' || (isWreck && veh.fireTimer > -20);
      firePool.visible = burning;
      if (burning) {
        firePool.scale.setScalar(7 + Math.sin(time * 23) * 0.8 + Math.sin(time * 7.3) * 0.6);
        firePool.updateMatrix();
      }
    },
    dispose() {
      if (shadow) freeShadow(shadow);
      if (ownsPaintGeo) disposeDentCopy(paintGeo);
      for (const s of slots) if (s) {
        setWheelRim(s, null);
        freeWheel(s);
      }
      releaseCarMaterial(material);
      if (matHigh) releaseCarMaterial(matHigh);
      if (matMid) releaseCarMaterial(matMid);
      glassMat.dispose();
      lightMat.dispose();
    },
  };
};

interface ViewState {
  rig: VehicleRig;
  /** `vehicle.data.lookVersion` the rig was built with (a workshop job rebuilds it). */
  look: unknown;
}

export function createVehicleViewSystem(): System {
  let ctx: GameContext;
  let vehicles: VehicleService;
  const views = new Map<number, ViewState>();
  const pose = { x: 0, z: 0, angle: 0 };
  let time = 0;
  const seen = new Set<number>();
  /** Where the wheel batches are (the city, or the studio while a car is shown on its own). */
  let wheelScene: THREE.Object3D | null = null;

  return {
    id: 'vehicle-view',
    order: 710,
    init(c) {
      ctx = c;
      vehicles = c.services.get('vehicles');
      attachWheelBatches(c.scene);
      attachShadowBatches(c.scene, c.render?.renderer ?? null);
      c.hooks.register('detailedCars', (on) => {
        if (on !== undefined && on !== meshyCarsEnabled.on) {
          meshyCarsEnabled.on = on;
          meshyCarsEnabled.version++;
        }
        return detailedNow;
      });
      wheelScene = c.scene;
    },
    update(dt, alpha) {
      time += dt;
      detailedNow = 0;
      carTime.value = time;
      // Rain film on every car (the render layer writes vehicleAmbient.wet from the weather).
      carWet.value = Math.max(0, vehicleAmbient.wet - 0.1) / 0.9;
      setWheelDetail(vehicleAmbient.tier);
      syncShadowBatches(Math.sqrt(FAR_DIST2[vehicleAmbient.tier]!));
      syncNeon(vehicleAmbient.night);
      const wheelHome = vehicleSolo.scene ?? ctx.scene;
      if (wheelScene !== wheelHome) {
        attachWheelBatches(wheelHome);
        wheelScene = wheelHome;
      }
      const live = vehicles.all();
      seen.clear();
      for (const v of live) {
        seen.add(v.id);
        let view = views.get(v.id);
        if (view && view.look !== v.data[CAR_LOOK_VERSION_KEY]) {
          view.rig.root.removeFromParent();
          view.rig.dispose();
          views.delete(v.id);
          view = undefined;
        }
        if (!view) {
          const factory = ctx.services.tryGet('vehicleMeshFactory') ?? defaultVehicleMeshFactory;
          const rig = factory(v);
          ctx.scene.add(rig.root);
          view = { rig, look: v.data[CAR_LOOK_VERSION_KEY] };
          views.set(v.id, view);
        }
        v.body.interpolate(alpha, pose);
        view.rig.root.position.set(pose.x, 0, pose.z);
        view.rig.root.rotation.y = pose.angle;
        const home = vehicleSolo.id === v.id && vehicleSolo.scene ? vehicleSolo.scene : ctx.scene;
        if (view.rig.root.parent !== home) {
          home.add(view.rig.root);
          view.rig.root.matrixWorldNeedsUpdate = true; // its matrix is kept by hand (keepMatrix): new parent, new world
        }
        view.rig.sync(v, dt, time);
      }
      for (const [id, view] of views) {
        if (seen.has(id)) continue;
        view.rig.root.removeFromParent();
        view.rig.dispose();
        views.delete(id);
      }
      if (!lodView.on) flushWheelBatches(); // else the render system does, with the frame's frustum
    },
    dispose() {
      for (const view of views.values()) {
        view.rig.root.removeFromParent();
        view.rig.dispose();
      }
      views.clear();
      detachWheelBatches();
      detachShadowBatches();
    },
  };
}
